import {
  Card,
  CascaderField,
  Col,
  DatePickerField,
  Flex,
  Form,
  HintIcon,
  LabeledText,
  Radio,
  Row,
  SelectField,
  Space,
  SubmitButton,
} from '@/components';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { t } from '@/i18n';
import { DayjsSchema } from '@/utils';
import {
  FundSheetTypeLabelModifier,
  SheetTypeLabelModifier,
  fundSheetTypeEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useInspectionSearchQueryStates } from '../_hooks/useInspectionSearchQueryStates';

const InspectionSearchFormSchema = z
  .object({
    fiscalYear: DayjsSchema(),
    sheetType: sheetTypeEnum,
    fundSheetType: fundSheetTypeEnum.nullable(),
    organizationIds: z.array(z.array(z.string())).nullable(),
  })
  .refine((data) => data.sheetType !== 'KS' || data.fundSheetType !== null, {
    path: ['fundSheetType'],
    message: t('validation.required'),
  });

type FormValues = z.infer<typeof InspectionSearchFormSchema>;

export const InspectionSearchForm: FC = () => {
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useInspectionSearchQueryStates();

  const { mutateAsync: createQueryStore } = useCreateQueryStore();
  const treeParamsData = useQueryStore(searchQueryStates.cascader ?? undefined);

  const defaultValues = useMemo(
    () => ({
      fiscalYear: dayjs().year(Number(searchQueryStates.fiscalYear)),
      sheetType: searchQueryStates.sheetType,
      fundSheetType: searchQueryStates.fundSheetType,
      organizationIds: treeParamsData.data?.query?.organizationIds ?? [],
    }),
    [searchQueryStates, treeParamsData.data],
  );

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(InspectionSearchFormSchema),
  });

  const sheetType = useWatch({ control, name: 'sheetType' });

  const handleSheetTypeChange = useCallback(
    (value: string) => {
      if (value === 'KS') {
        setValue('fundSheetType', 'standard');
      } else {
        setValue('fundSheetType', null);
      }
    },
    [setValue],
  );

  useEffect(() => {
    // cascaderは非同期処理なので取得できたタイミングでセットする
    if (defaultValues.organizationIds?.length) {
      setValue('organizationIds', defaultValues.organizationIds);
    }
  }, [setValue, defaultValues.organizationIds]);

  const organizationsOptions = useOrganizationOptionsRecursive({
    params: {
      referenceFiscalYear: searchQueryStates.fiscalYear ?? undefined,
    },
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const currentCascaderValues = {
        organizationIds: defaultValues.organizationIds,
      };
      const newQueryStore = generateQueryAndLeafs({
        organizationIds: values.organizationIds,
      });

      const prevCascaderId = searchQueryStates.cascader;
      const hasChanged = Object.keys(currentCascaderValues).some((key) => {
        const newQuery = newQueryStore?.query[key];
        const currentCascaderValue = currentCascaderValues[key];
        return !isEqual(newQuery, currentCascaderValue);
      });

      const isDifferent =
        hasChanged && !isEqual(newQueryStore?.query, currentCascaderValues);

      const newCascadeId =
        isDifferent && newQueryStore
          ? (await createQueryStore(newQueryStore)).id
          : newQueryStore
            ? prevCascaderId
            : null;

      setSearchQueryStates((v) => ({
        ...v,
        fiscalYear: values.fiscalYear?.year().toString() ?? null,
        sheetType: values.sheetType ?? null,
        fundSheetType:
          values.sheetType === 'KS' ? (values.fundSheetType ?? null) : null,
        cascader: newCascadeId,
        page: 1,
      }));
    },
    [
      setSearchQueryStates,
      searchQueryStates.cascader,
      defaultValues.organizationIds,
      createQueryStore,
    ],
  );

  return (
    <Card style={{ borderRadius: 0 }}>
      <Form
        leavePageAlertCondition={null}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <Flex vertical gap={16}>
          <Row gutter={16}>
            <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
              <LabeledText
                label={t('inspection-type')}
                labelVariant="body"
                labelColor="secondary"
                labelWeight="regular"
                text={
                  <Space size="small">
                    <Radio checked>{t('republish-check')}</Radio>
                    <HintIcon hint={t('republish-check-tooltip')} />
                  </Space>
                }
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
              <DatePickerField
                name="fiscalYear"
                label={t('fiscal-year')}
                control={control}
                errors={errors}
                picker="year"
                allowClear={false}
                fieldStyle={{ width: '100%' }}
                required
              />
            </Col>
            <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
              <SelectField
                name="sheetType"
                label={t('sheet-type')}
                control={control}
                errors={errors}
                options={sheetTypeEnum.options.map((value) => ({
                  value,
                  label: SheetTypeLabelModifier[value],
                }))}
                onChange={handleSheetTypeChange}
                shouldCoexistOnChange
                required
              />
            </Col>
            {sheetType === 'KS' && (
              <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
                <SelectField
                  name="fundSheetType"
                  label={t('fund-sheet-type')}
                  control={control}
                  errors={errors}
                  required
                  options={fundSheetTypeEnum.options.map((value) => ({
                    value,
                    label: FundSheetTypeLabelModifier[value],
                  }))}
                  allowClear={false}
                />
              </Col>
            )}
            <Col span={8} style={{ minWidth: COL_MIN_WIDTH.large }}>
              <CascaderField
                name="organizationIds"
                label={t('organization-in-charge')}
                control={control}
                options={organizationsOptions.data}
                loading={organizationsOptions.isPending}
                multiple
                errors={errors}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
              <SubmitButton text={t('search-by')} loading={isSubmitting} />
            </Col>
          </Row>
        </Flex>
      </Form>
    </Card>
  );
};
